import React, { useState, useEffect, useMemo } from 'react';
import { getData, getDataWithComp, useEscapeClose } from '../../../utils/SamuiUtils';

const ArModal = ({
    showArModal,
    handleArClose,
    arDataList,
    onRowSelectAr
}) => {
    // สถานะการค้นหา
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredArDataList, setFilteredArDataList] = useState(arDataList);
    const [selectedRowIndex, setSelectedRowIndex] = useState(0); // เพิ่ม state สำหรับเลือกแถว

    // ตัวแปรเก็บข้อมูลของสายส่ง
    const [deliveryOptions, setDeliveryOptions] = useState([]);
    const [deliveryId, setDeliveryId] = useState('');
    const [dlLineList, setDlLineList] = useState([]);

    useEscapeClose(showArModal, handleArClose);

    const visibleArDataList = useMemo(
        () => filteredArDataList.slice(0, 1000),
        [filteredArDataList]
    );

    const matchesSearchTerm = (ar) => {
        const keyword = searchTerm.toLowerCase();
        return (
            String(ar.AR_Code || '').toLowerCase().includes(keyword) ||
            String(ar.AR_Name || '').toLowerCase().includes(keyword)
        );
    };

    // ฟิลเตอร์ข้อมูลเมื่อ searchTerm หรือ arDataList เปลี่ยนแปลง
    useEffect(() => {
        if (!deliveryId) {
            // ไม่ได้เลือกสายส่ง
            setFilteredArDataList(
                arDataList.filter(matchesSearchTerm)
            );
        } else {
            // ถ้าเลือกสายส่ง ให้กรองจาก View_DL_Line
            const filtered = arDataList.filter(ar => {
                return dlLineList.some(dl => Number(dl.DL_AR_Id) === Number(ar.AR_Id)) &&
                    matchesSearchTerm(ar);
            });
            setFilteredArDataList(filtered);
        }
    }, [searchTerm, arDataList, deliveryId, dlLineList]);

    useEffect(() => {
        if (showArModal) {
            setSearchTerm('');
            setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อเปิด modal
        }
    }, [showArModal]);

    useEffect(() => {
        const refreshSelectedDeliveryLine = async () => {
            if (!showArModal || !deliveryId) return;

            try {
                const response = await getData("View_DL_Line", `DL_Id = ${deliveryId} ORDER BY DL_Queue ASC`);
                setDlLineList(response || []);
            } catch (err) {
                console.error('Error refreshing View_DL_Line:', err);
                setDlLineList([]);
            }
        };

        refreshSelectedDeliveryLine();
    }, [showArModal, deliveryId, arDataList]);

    // เพิ่ม keyboard navigation สำหรับ modal
    useEffect(() => {
        const handleKeyDown = (event) => {
            // ทำงานเฉพาะเมื่อ modal เปิดอยู่
            if (!showArModal) return;

            // ป้องกันการทำงานถ้า focus อยู่ใน input search หรือ select
            if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'SELECT') {
                // ยกเว้น Enter, ESC, F5 และ ArrowDown ที่ยังให้ทำงานได้ใน input/select
                if (event.key !== 'Enter' && event.key !== 'Escape' && event.key !== 'F5' && event.key !== 'ArrowDown') {
                    return;
                }
            }

            if (filteredArDataList.length === 0) return;

            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex > 0 ? prevIndex - 1 : filteredArDataList.length - 1;
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        setTimeout(() => scrollToRow(newIndex), 0);
                        return newIndex;
                    });
                    break;

                case 'ArrowDown':
                    event.preventDefault();
                    // ถ้าอยู่ใน input/select และกดลูกศรลง ให้ focus ไปที่ตารางและเลือกแถวแรก
                    if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'SELECT') {
                        setSelectedRowIndex(0);
                        setTimeout(() => scrollToRow(0), 0);
                        // เอา focus ออกจาก input/select
                        document.activeElement.blur();
                        return;
                    }
                    
                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex < filteredArDataList.length - 1 ? prevIndex + 1 : 0;
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        setTimeout(() => scrollToRow(newIndex), 0);
                        return newIndex;
                    });
                    break;

                case 'Enter':
                    event.preventDefault();
                    if (filteredArDataList[selectedRowIndex]) {
                        onRowSelectAr(filteredArDataList[selectedRowIndex]);
                    }
                    break;

                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation(); // ป้องกันไม่ให้ event ไปยัง parent elements
                    event.stopImmediatePropagation(); // ป้องกันไม่ให้ listener อื่นทำงาน
                    handleArClose();
                    return false; // ป้องกันการ bubble ขึ้นไป
                    break;

                case 'Home':
                    event.preventDefault();
                    setSelectedRowIndex(0);
                    setTimeout(() => scrollToRow(0), 0);
                    break;

                case 'F5':
                    event.preventDefault();
                    const searchInput = document.querySelector('.modal.show input[type="text"]');
                    
                    // ถ้า focus อยู่ใน input/select อยู่แล้ว ให้เอา focus ออกเพื่อใช้ลูกศรได้
                    if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'SELECT') {
                        document.activeElement.blur();
                        setSelectedRowIndex(0); // เลือกแถวแรก
                        setTimeout(() => scrollToRow(0), 0);
                    } else {
                        // ถ้าไม่ได้ focus ใน input ให้ focus ช่องค้นหา
                        if (searchInput) {
                            searchInput.focus();
                        }
                    }
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown, true); // ใช้ capture phase เพื่อให้ทำงานก่อน

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [showArModal, filteredArDataList, selectedRowIndex, onRowSelectAr, handleArClose]);

    // ฟังก์ชันสำหรับเลื่อน scroll ไปยังแถวที่เลือก
    const scrollToRow = (rowIndex) => {
        const tableContainer = document.querySelector('.table-responsive');
        const selectedRow = document.querySelector(`tbody tr:nth-child(${rowIndex + 1})`);
        
        if (tableContainer && selectedRow) {
            const containerRect = tableContainer.getBoundingClientRect();
            const rowRect = selectedRow.getBoundingClientRect();
            
            // คำนวณตำแหน่งที่ต้องเลื่อน
            const rowTop = selectedRow.offsetTop;
            const rowHeight = selectedRow.offsetHeight;
            const containerHeight = tableContainer.clientHeight;
            const scrollTop = tableContainer.scrollTop;
            
            // ตรวจสอบว่าแถวอยู่นอกเขตมองเห็นหรือไม่
            if (rowTop < scrollTop) {
                // แถวอยู่เหนือเขตมองเห็น - เลื่อนขึ้น
                tableContainer.scrollTop = rowTop - 10; // เผื่อ padding
            } else if (rowTop + rowHeight > scrollTop + containerHeight) {
                // แถวอยู่ใต้เขตมองเห็น - เลื่อนลง
                tableContainer.scrollTop = rowTop + rowHeight - containerHeight + 10; // เผื่อ padding
            }
        }
    };

    // รีเซ็ต selectedRowIndex เมื่อ filteredArDataList เปลี่ยน
    useEffect(() => {
        setSelectedRowIndex(0);
    }, [filteredArDataList]);

    useEffect(() => {
        const fetchDeliveryOptions = async () => {
            try {
                const response = await getDataWithComp('DL_Line_H', 'ORDER By DL_Id ASC');

                const options = response.map((item) => ({
                    id: item.DL_Id,
                    label: item.DL_LineName,
                }));

                setDeliveryOptions(options);
            } catch (err) {
                console.error('Error fetching delivery options:', err);
            }
        };

        fetchDeliveryOptions();
    }, []);

    const handleChange = async (event) => {
        const selectedValue = event.target.value;
        setDeliveryId(selectedValue);

        if (!selectedValue) {
            setDlLineList([]);
            return;
        }

        try {
            const response = await getData("View_DL_Line", `DL_Id = ${selectedValue} ORDER BY DL_Queue ASC`);
            setDlLineList(response);
        } catch (err) {
            console.error('Error fetching View_DL_Line:', err);
            setDlLineList([]);
        }
    };

    return (
        <>
            <div className={`modal ${showArModal ? 'show' : ''}`} style={{ display: showArModal ? 'block' : 'none' }} tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">รายชื่อลูกค้า</h5>
                            <button type="button" className="close" onClick={handleArClose}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">

                            {window.location.pathname === '/picking-list' ? (
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label>ค้นหาลูกค้า</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="ค้นหา รหัสลูกค้า หรือ ชื่อลูกค้า"
                                                value={searchTerm || ''}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-group">
                                            <label htmlFor="select-delivery">สายส่ง</label>
                                            <select
                                                id="select-delivery"
                                                className="form-select"
                                                value={deliveryId}
                                                onChange={handleChange}
                                            >
                                                <option value="">เลือกสายส่ง</option>
                                                {deliveryOptions.map((option) => (
                                                    <option key={option.id} value={option.id.toString()}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="form-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="ค้นหา รหัสลูกค้า หรือ ชื่อลูกค้า"
                                        value={searchTerm || ''}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            )}

                            <div className="table-responsive" style={{ maxHeight: '650px', overflowY: 'auto' }}>
                                <table className="table table-striped table-hover">
                                    <thead className="thead-dark">
                                        <tr>
                                            <th className="text-center" style={{ width: '139px' }}>รหัสลูกค้า</th>
                                            <th className="text-center" style={{ width: '620px' }}>ชื่อลูกค้า</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredArDataList && filteredArDataList.length > 0 ? (
                                            visibleArDataList.map((ar, index) => (
                                                <tr
                                                    key={ar.AR_Id || index + 1}
                                                    onClick={() => {
                                                        setSelectedRowIndex(index);
                                                        onRowSelectAr(ar);
                                                    }}
                                                    style={{ 
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedRowIndex === index ? 'rgb(105, 188, 216)' : 'transparent',
                                                        transition: 'background-color 0.3s ease'
                                                    }}>
                                                    <td className="text-center">{ar.AR_Code}</td>
                                                    <td style={{ textAlign: 'left' }}>{ar.AR_Name}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="10">
                                                    <center>
                                                        <h5>ไม่พบข้อมูล</h5>
                                                    </center>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <small className="text-muted me-auto">
                                <i className="fas fa-keyboard me-1"></i>
                                <strong>คีย์ลัด:</strong> ↑↓ เลือกแถว | Enter เลือก | ESC ปิด
                            </small>
                            <button className="btn btn-danger" onClick={handleArClose}>
                                ปิด (ESC)
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {/* Overlay */}
            {showArModal && <div className="modal-backdrop fade show"></div>}
        </>
    );
};

export default ArModal;
